import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Tooltip as MuiTooltip,
  LinearProgress,
  Stack,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Collapse,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import DownloadIcon from '@mui/icons-material/Download';
import { format } from 'date-fns';
import {
  getMigrationProgress,
  getMigrationClientsByWeek,
  getAzureMigrationPerformance,
  getMigrationPerfSyncStatus,
  triggerMigrationPerfSync,
  getMigrationPerfSnapshots
} from '../../services/apiService';
import { pctDelta } from './azureMigrationComparisonUtils';

// ─── Constants ───────────────────────────────────────────────────────────────

const COLO_COLOR  = '#D74B00'; // "Before" warm orange — matches performance tab
const AZURE_COLOR = '#1D4434'; // "After" deep green — matches performance tab
const GOOD_BG = '#e8f5e9';
const GOOD_FG = '#2e7d32';
const BAD_BG = '#ffebee';
const BAD_FG = '#c62828';
const NEUTRAL_BG = '#f5f5f5';
const NEUTRAL_FG = '#616161';

/** Max simultaneous Datadog calls — keeps server load reasonable. */
const CONCURRENCY = 4;

/**
 * Metrics to compare in the cohort table.
 * All deltas are (azure - colo) / |colo| * 100.
 */
const METRIC_DEFS = [
  {
    key: 'p99',
    label: 'App Response (worst 1%)',
    tooltip: 'Servlet p99 latency — the slowest 1% of app requests. A high value means your heaviest users are waiting longest. Lower is better.',
    coloField: 'servletP99Seconds',
    azureField: 'servletP99Seconds',
    lowerBetter: true,
    band: 15,
    unit: 'ms',
    mult: 1000,
    decimals: 1
  },
  {
    key: 'err',
    label: 'Error Rate',
    tooltip: 'Mean daily error rate (%). Lower is better.',
    coloField: 'errorRatePct',
    azureField: 'errorRatePct',
    lowerBetter: true,
    band: 25,
    unit: '%',
    mult: 1,
    decimals: 3
  },
  {
    key: 'http',
    label: 'Outbound HTTP',
    tooltip: 'Average outbound HTTP call duration. Lower is better.',
    coloField: 'httpSeconds',
    azureField: 'httpSeconds',
    lowerBetter: true,
    band: 15,
    unit: 'ms',
    mult: 1000,
    decimals: 1
  },
  {
    key: 'mem',
    label: 'Memory',
    tooltip: 'Usable host memory %. Higher is better (more free memory = less pressure).',
    coloField: 'memPctUsable',
    azureField: 'memPctUsable',
    lowerBetter: false,
    band: 10,
    unit: '%',
    mult: 1,
    decimals: 2
  },
  {
    key: 'apacheApi',
    label: 'API Response Time',
    tooltip: 'Real user response time from Apache access logs — API / integration calls (@call:api). Already in ms. Lower is better.',
    fromApache: 'api',   // read from res.apache.api.{colo|azure}.avgMs
    lowerBetter: true,
    band: 15,
    unit: 'ms',
    mult: 1,             // already ms — no conversion
    decimals: 1
  },
  {
    key: 'apacheUi',
    label: 'UI Response Time',
    tooltip: 'Real user response time from Apache access logs — browser / UI requests. Already in ms. Lower is better.',
    fromApache: 'ui',    // read from res.apache.ui.{colo|azure}.avgMs
    lowerBetter: true,
    band: 15,
    unit: 'ms',
    mult: 1,
    decimals: 1
  }
];

// ─── Pure helpers ─────────────────────────────────────────────────────────────

function isoDayToLocalDate(iso) {
  if (!iso || typeof iso !== 'string') return null;
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  const local = new Date(y, m - 1, d);
  return Number.isNaN(local.getTime()) ? null : local;
}

function formatWeekRange(weekIso) {
  const start = isoDayToLocalDate(weekIso);
  if (!start) return weekIso || '';
  const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6);
  const sameMonth =
    start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
  const sameYear = start.getFullYear() === end.getFullYear();
  if (sameMonth) return `${format(start, 'd')}\u2013${format(end, 'd MMM yyyy')}`;
  if (sameYear) return `${format(start, 'd MMM')}\u2013${format(end, 'd MMM yyyy')}`;
  return `${format(start, 'd MMM yyyy')}\u2013${format(end, 'd MMM yyyy')}`;
}


/** Run async tasks array with a concurrency cap; calls onProgress(done, total) after each. */
async function runWithConcurrency(tasks, concurrency, onProgress) {
  const results = new Array(tasks.length).fill(null);
  let nextIndex = 0;
  let done = 0;

  async function worker() {
    while (nextIndex < tasks.length) {
      const idx = nextIndex++;
      try {
        results[idx] = { ok: true, value: await tasks[idx]() };
      } catch (err) {
        results[idx] = { ok: false, error: err?.message || 'Request failed' };
      }
      onProgress(++done, tasks.length);
    }
  }

  const pool = Array.from({ length: Math.min(concurrency, tasks.length) }, () => worker());
  await Promise.all(pool);
  return results;
}

/** Format a % delta for display, switching to ratio for very large values. */
function formatDelta(delta) {
  if (delta == null || !Number.isFinite(delta)) return '\u2014';
  const sign = delta > 0 ? '+' : '';
  const abs = Math.abs(delta);
  if (abs <= 999) return `${sign}${delta.toFixed(1)}%`;
  const ratio = 1 + delta / 100;
  return ratio >= 1 ? `${ratio.toFixed(1)}\u00d7\u2191` : `${(1 / ratio).toFixed(1)}\u00d7\u2193`;
}

/** Format a raw value for a metric (applies mult + unit). */
function formatVal(val, def) {
  if (val == null || !Number.isFinite(val)) return '\u2014';
  return `${(val * def.mult).toFixed(def.decimals)} ${def.unit}`;
}

/** Classify a delta as 'better' | 'worse' | 'similar' | 'no-data'. */
function classifyDelta(delta, lowerBetter, band) {
  if (delta == null || !Number.isFinite(delta)) return 'no-data';
  if (Math.abs(delta) <= band) return 'similar';
  return (delta < 0) === lowerBetter ? 'better' : 'worse';
}

/** Compute per-client metric summaries from a raw performance API response. */
function computeClientSummary(perfData) {
  if (!perfData?.summary?.colo || !perfData?.summary?.azure) return null;
  const { colo, azure } = perfData.summary;
  const apache = perfData.apache;

  const metrics = {};
  for (const m of METRIC_DEFS) {
    let coloVal, azureVal;

    if (m.fromApache) {
      // Apache access-log data lives in res.apache[split].{colo|azure}.avgMs (already ms)
      coloVal = apache?.[m.fromApache]?.colo?.avgMs ?? null;
      azureVal = apache?.[m.fromApache]?.azure?.avgMs ?? null;
    } else {
      coloVal = colo?.[m.coloField] ?? null;
      azureVal = azure?.[m.azureField] ?? null;
    }

    const delta = pctDelta(coloVal, azureVal);
    metrics[m.key] = {
      colo: coloVal,
      azure: azureVal,
      delta,
      verdict: classifyDelta(delta, m.lowerBetter, m.band)
    };
  }

  // Overall verdict: response time (p99, apacheApi, apacheUi) is the primary factor.
  // Supporting metrics (err, http, mem) act as tiebreakers when RT is evenly split.
  const RT_KEYS = new Set(['p99', 'apacheApi', 'apacheUi']);
  const rtVerdicts = Object.entries(metrics)
    .filter(([k, m]) => RT_KEYS.has(k) && m.verdict !== 'no-data')
    .map(([, m]) => m.verdict);
  const supportVerdicts = Object.entries(metrics)
    .filter(([k, m]) => !RT_KEYS.has(k) && m.verdict !== 'no-data')
    .map(([, m]) => m.verdict);

  const rtBetter = rtVerdicts.filter((v) => v === 'better').length;
  const rtWorse = rtVerdicts.filter((v) => v === 'worse').length;
  const rtTotal = rtVerdicts.length;

  let overall;
  if (rtTotal === 0 && supportVerdicts.length === 0) {
    overall = 'no-data';
  } else if (rtTotal === 0) {
    // No RT data — fall back to supporting metrics only
    const sb = supportVerdicts.filter((v) => v === 'better').length;
    const sw = supportVerdicts.filter((v) => v === 'worse').length;
    const st = supportVerdicts.length;
    const sm = Math.ceil(st / 2);
    if (sb >= sm) overall = sb === st ? 'better' : 'mostly-better';
    else if (sw >= sm) overall = sw === st ? 'worse' : 'mostly-worse';
    else overall = 'mixed';
  } else {
    const rtMajority = Math.ceil(rtTotal / 2);
    if (rtWorse >= rtMajority) {
      // RT majority is worse — verdict is bad regardless of supporting metrics
      overall = rtWorse === rtTotal ? 'worse' : 'mostly-worse';
    } else if (rtBetter >= rtMajority) {
      overall = rtBetter === rtTotal ? 'better' : 'mostly-better';
    } else {
      // RT is evenly split — use all metrics combined to break tie
      const allVerdicts = [...rtVerdicts, ...supportVerdicts];
      const ab = allVerdicts.filter((v) => v === 'better').length;
      const aw = allVerdicts.filter((v) => v === 'worse').length;
      const at = allVerdicts.length;
      const am = Math.ceil(at / 2);
      if (ab >= am) overall = 'mostly-better';
      else if (aw >= am) overall = 'mostly-worse';
      else overall = 'mixed';
    }
  }

  const betterCount = [...rtVerdicts, ...supportVerdicts].filter((v) => v === 'better').length;
  const worseCount = [...rtVerdicts, ...supportVerdicts].filter((v) => v === 'worse').length;

  return { metrics, overall, betterCount, worseCount, meta: perfData.meta };
}

// ─── Cohort HTML export ───────────────────────────────────────────────────────

const RT_EXPORT_KEYS = ['p99', 'apacheApi', 'apacheUi'];
const SUPPORT_EXPORT_KEYS = ['err', 'http', 'mem'];

function generateCohortReportHtml(clients, reports, selectedWeek) {
  const weekRange = formatWeekRange(selectedWeek);
  const generatedAt = new Date().toLocaleString('en-GB', { dateStyle: 'long', timeStyle: 'short' });

  const doneClients = clients.filter(
    (c) => reports[c.instanceName]?.status === 'done' && reports[c.instanceName]?.summary
  );

  // Sort: worse first, then mostly-worse, mixed, mostly-better, better, no-data
  const ORDER = { worse: 0, 'mostly-worse': 1, mixed: 2, 'mostly-better': 3, better: 4, 'no-data': 5 };
  const sortedClients = [...clients].sort((a, b) => {
    const oa = ORDER[reports[a.instanceName]?.summary?.overall ?? 'no-data'] ?? 5;
    const ob = ORDER[reports[b.instanceName]?.summary?.overall ?? 'no-data'] ?? 5;
    return oa - ob;
  });

  const verdicts = doneClients.map((c) => reports[c.instanceName].summary.overall);
  const betterTotal = verdicts.filter((v) => v === 'better' || v === 'mostly-better').length;
  const worseTotal = verdicts.filter((v) => v === 'worse' || v === 'mostly-worse').length;
  const mixedTotal = verdicts.filter((v) => v === 'mixed').length;
  const noDataTotal = clients.length - doneClients.length;

  const rtMetaDefs = RT_EXPORT_KEYS.map((k) => METRIC_DEFS.find((d) => d.key === k));
  const supportMetaDefs = SUPPORT_EXPORT_KEYS.map((k) => METRIC_DEFS.find((d) => d.key === k));

  const rtTallyCards = rtMetaDefs.map((m) => {
    const better = doneClients.filter((c) => reports[c.instanceName].summary.metrics[m.key]?.verdict === 'better').length;
    const worse = doneClients.filter((c) => reports[c.instanceName].summary.metrics[m.key]?.verdict === 'worse').length;
    const similar = doneClients.filter((c) => reports[c.instanceName].summary.metrics[m.key]?.verdict === 'similar').length;
    const noD = doneClients.filter((c) => reports[c.instanceName].summary.metrics[m.key]?.verdict === 'no-data').length;
    return `
      <div class="metric-card">
        <div class="metric-card-title">${m.label}</div>
        <div class="metric-tally">
          <span class="tally-better">${better} better</span>
          <span class="tally-worse">${worse} worse</span>
          ${similar > 0 ? `<span class="tally-similar">${similar} similar</span>` : ''}
          ${noD > 0 ? `<span class="tally-nodata">${noD} no data</span>` : ''}
        </div>
      </div>`;
  }).join('');

  const verdictBadge = (verdict) => {
    const map = {
      better: { text: 'Better on Azure', bg: '#e8f5e9', fg: '#1b5e20', border: '#81c784' },
      'mostly-better': { text: 'Mostly better', bg: '#f1f8e9', fg: '#33691e', border: '#aed581' },
      worse: { text: 'Worse on Azure', bg: '#ffebee', fg: '#b71c1c', border: '#ef9a9a' },
      'mostly-worse': { text: 'Mostly worse', bg: '#fff3e0', fg: '#bf360c', border: '#ffcc80' },
      mixed: { text: 'Mixed results', bg: '#fff8e1', fg: '#e65100', border: '#ffe082' },
      'no-data': { text: 'No data', bg: '#f5f5f5', fg: '#616161', border: '#e0e0e0' }
    };
    const v = map[verdict] || map['no-data'];
    return `<span style="background:${v.bg};color:${v.fg};border:1px solid ${v.border};font-weight:700;font-size:11px;padding:3px 9px;border-radius:12px;white-space:nowrap;display:inline-block">${v.text}</span>`;
  };

  const deltaCell = (metric) => {
    if (!metric || metric.delta == null || !Number.isFinite(metric.delta)) {
      return '<td class="td-metric td-nodata">—</td>';
    }
    const { delta, verdict } = metric;
    const sign = delta > 0 ? '+' : '';
    const abs = Math.abs(delta);
    let text;
    if (abs <= 999) text = `${sign}${delta.toFixed(1)}%`;
    else {
      const ratio = 1 + delta / 100;
      text = ratio >= 1 ? `${ratio.toFixed(1)}×↑` : `${(1 / ratio).toFixed(1)}×↓`;
    }
    const color = verdict === 'better' ? '#2e7d32' : verdict === 'worse' ? '#c62828' : '#616161';
    const bg = verdict === 'better' ? '#e8f5e9' : verdict === 'worse' ? '#ffebee' : 'transparent';
    const fw = verdict === 'no-data' ? 400 : 700;
    return `<td class="td-metric"><span style="color:${color};background:${bg};font-weight:${fw};padding:2px 5px;border-radius:4px">${text}</span></td>`;
  };

  const rows = sortedClients.map((client) => {
    const report = reports[client.instanceName];
    const summary = report?.summary;
    const overall = summary?.overall ?? 'no-data';
    const rowBg = overall === 'worse' || overall === 'mostly-worse' ? '#fff8f8' :
                  overall === 'better' || overall === 'mostly-better' ? '#f8fff9' : '';

    const statusBadge = !report || report.status === 'pending'
      ? '<td colspan="8" style="color:#999;font-style:italic">No data loaded</td>'
      : report.status === 'error'
        ? `<td colspan="8" style="color:#c62828;font-style:italic">Load failed: ${report.error || 'unknown error'}</td>`
        : '';

    if (statusBadge) {
      return `<tr style="background:${rowBg}">
        <td class="td-name">${client.instanceName}</td>
        <td class="td-env">${client.sourceDatacenter || '—'}</td>
        <td class="td-env">${client.currentDatacenter || '—'}</td>
        ${statusBadge}
        <td>${verdictBadge('no-data')}</td>
      </tr>`;
    }

    const m = summary.metrics;
    return `<tr style="background:${rowBg}">
      <td class="td-name">${client.instanceUrl
        ? `<a href="${client.instanceUrl}" style="color:#1565c0;text-decoration:none">${client.instanceName}</a>`
        : client.instanceName}</td>
      <td class="td-env" style="color:#b45309">${client.sourceDatacenter || '—'}</td>
      <td class="td-env" style="color:#0d47a1">${client.currentDatacenter || '—'}</td>
      ${deltaCell(m.p99)}
      ${deltaCell(m.apacheApi)}
      ${deltaCell(m.apacheUi)}
      ${deltaCell(m.err)}
      ${deltaCell(m.http)}
      ${deltaCell(m.mem)}
      <td style="text-align:center">${verdictBadge(overall)}</td>
    </tr>`;
  }).join('');

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<title>Azure Migration Cohort Report – ${weekRange}</title>
<style>
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif; color: #1a1a2e; background: #f0f4f8; font-size: 13px; }
  .header { background: linear-gradient(135deg, #0d47a1 0%, #0288d1 60%, #26c6da 100%); color: white; padding: 36px 48px 28px; }
  .header h1 { font-size: 26px; font-weight: 800; letter-spacing: -0.5px; }
  .header-sub { margin-top: 6px; opacity: 0.88; font-size: 14px; }
  .header-meta { margin-top: 14px; display: flex; gap: 16px; flex-wrap: wrap; }
  .header-badge { background: rgba(255,255,255,0.18); border: 1px solid rgba(255,255,255,0.3); border-radius: 20px; padding: 4px 14px; font-size: 12px; font-weight: 600; }
  .section { max-width: 1100px; margin: 24px auto; padding: 0 24px; }
  .section-title { font-size: 16px; font-weight: 800; color: #0d47a1; margin-bottom: 14px; padding-bottom: 6px; border-bottom: 2px solid #bbdefb; }
  .kpi-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin-bottom: 24px; }
  .kpi-card { border-radius: 10px; padding: 16px 18px; border: 1px solid; }
  .kpi-card.better { background: #e8f5e9; border-color: #a5d6a7; }
  .kpi-card.worse { background: #ffebee; border-color: #ef9a9a; }
  .kpi-card.mixed { background: #fff8e1; border-color: #ffe082; }
  .kpi-card.nodata { background: #f5f5f5; border-color: #e0e0e0; }
  .kpi-number { font-size: 36px; font-weight: 900; line-height: 1; }
  .kpi-card.better .kpi-number { color: #1b5e20; }
  .kpi-card.worse .kpi-number { color: #b71c1c; }
  .kpi-card.mixed .kpi-number { color: #e65100; }
  .kpi-card.nodata .kpi-number { color: #616161; }
  .kpi-label { font-size: 12px; font-weight: 600; margin-top: 4px; color: inherit; }
  .kpi-card.better .kpi-label { color: #2e7d32; }
  .kpi-card.worse .kpi-label { color: #c62828; }
  .kpi-card.mixed .kpi-label { color: #e65100; }
  .kpi-card.nodata .kpi-label { color: #757575; }
  .primary-note { background: #e3f2fd; border: 1px solid #90caf9; border-radius: 8px; padding: 10px 16px; font-size: 12px; color: #0d47a1; margin-bottom: 20px; }
  .metrics-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-bottom: 8px; }
  .metric-card { background: white; border: 1px solid #e0e0e0; border-radius: 8px; padding: 12px 14px; }
  .metric-card-title { font-size: 11px; font-weight: 700; color: #0d47a1; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.3px; }
  .metric-tally { display: flex; gap: 6px; flex-wrap: wrap; }
  .tally-better { background: #e8f5e9; color: #2e7d32; font-weight: 700; font-size: 11px; padding: 2px 8px; border-radius: 10px; }
  .tally-worse { background: #ffebee; color: #c62828; font-weight: 700; font-size: 11px; padding: 2px 8px; border-radius: 10px; }
  .tally-similar { background: #f5f5f5; color: #616161; font-weight: 600; font-size: 11px; padding: 2px 8px; border-radius: 10px; }
  .tally-nodata { background: #fafafa; color: #9e9e9e; font-size: 11px; padding: 2px 8px; border-radius: 10px; }
  table { width: 100%; border-collapse: collapse; background: white; border-radius: 10px; overflow: hidden; box-shadow: 0 1px 4px rgba(0,0,0,0.08); }
  thead tr { background: linear-gradient(90deg, #1565c0, #0288d1); color: white; }
  th { padding: 10px 9px; font-size: 11px; font-weight: 700; text-align: left; white-space: nowrap; }
  th.th-rt { background: rgba(255,255,255,0.12); }
  tr:nth-child(even) { background: #fafbfc; }
  td { padding: 8px 9px; font-size: 12px; border-bottom: 1px solid #f0f0f0; vertical-align: middle; }
  .td-name { font-weight: 600; min-width: 160px; }
  .td-env { font-size: 11px; font-weight: 600; white-space: nowrap; }
  .td-metric { text-align: center; white-space: nowrap; }
  .td-nodata { text-align: center; color: #bbb; }
  .col-group-label { font-size: 10px; opacity: 0.8; font-weight: 400; display: block; }
  .footer { text-align: center; padding: 28px; color: #9e9e9e; font-size: 11px; }
  @media print {
    body { background: white; }
    .section { max-width: 100%; padding: 0 12px; }
    .header { padding: 20px 24px; }
    table { box-shadow: none; border: 1px solid #e0e0e0; }
    th { font-size: 10px; padding: 7px 6px; }
    td { font-size: 11px; padding: 6px 6px; }
  }
</style>
</head>
<body>

<div class="header">
  <h1>Azure Migration Cohort Report</h1>
  <div class="header-sub">Weekly performance comparison — Azure vs Colo (${clients.length > 0 ? `${clients.length} client${clients.length !== 1 ? 's' : ''}` : 'no clients'})</div>
  <div class="header-meta">
    <div class="header-badge">📅 Week of ${weekRange}</div>
    <div class="header-badge">✅ ${doneClients.length} clients loaded</div>
    <div class="header-badge">🕒 Generated ${generatedAt}</div>
    <div class="header-badge">📊 HighQ Metrics Portal</div>
  </div>
</div>

<div class="section">
  <div class="section-title">Executive Summary</div>
  <div class="kpi-grid">
    <div class="kpi-card better">
      <div class="kpi-number">${betterTotal}</div>
      <div class="kpi-label">Better on Azure</div>
    </div>
    <div class="kpi-card worse">
      <div class="kpi-number">${worseTotal}</div>
      <div class="kpi-label">Worse on Azure</div>
    </div>
    <div class="kpi-card mixed">
      <div class="kpi-number">${mixedTotal}</div>
      <div class="kpi-label">Mixed results</div>
    </div>
    <div class="kpi-card nodata">
      <div class="kpi-number">${noDataTotal}</div>
      <div class="kpi-label">No data / load failed</div>
    </div>
  </div>
</div>

<div class="section">
  <div class="section-title">Response Time Analysis (Primary Factor)</div>
  <div class="primary-note">
    ⚡ Overall verdict is driven by <strong>response time metrics</strong> (App p99, Apache API, Apache UI).
    Error rate, outbound HTTP, and memory are supporting indicators.
  </div>
  <div class="metrics-grid">${rtTallyCards}</div>
</div>

<div class="section">
  <div class="section-title">Client Performance Details</div>
  <table>
    <thead>
      <tr>
        <th rowspan="2">Instance</th>
        <th rowspan="2">Colo</th>
        <th rowspan="2">Azure</th>
        <th colspan="3" style="text-align:center;border-left:2px solid rgba(255,255,255,0.3)">
          Response Time (Primary)
        </th>
        <th colspan="3" style="text-align:center;border-left:2px solid rgba(255,255,255,0.2)">
          Supporting Metrics
        </th>
        <th rowspan="2" style="text-align:center">Overall</th>
      </tr>
      <tr>
        <th class="th-rt" style="border-left:2px solid rgba(255,255,255,0.3)">App p99</th>
        <th class="th-rt">API Avg</th>
        <th class="th-rt">UI Avg</th>
        <th style="border-left:2px solid rgba(255,255,255,0.2)">Error Rate</th>
        <th>Outbound HTTP</th>
        <th>Memory</th>
      </tr>
    </thead>
    <tbody>${rows}</tbody>
  </table>
  <div style="margin-top:10px;font-size:11px;color:#757575">
    % = Azure vs Colo change. <span style="color:#2e7d32;font-weight:700">Green</span> = Azure improved &nbsp;·&nbsp;
    <span style="color:#c62828;font-weight:700">Red</span> = Azure worse &nbsp;·&nbsp; Grey = within noise band.
    Sorted: worse clients first for quick action.
  </div>
</div>

<div class="footer">
  Generated by HighQ Metrics Portal &nbsp;·&nbsp; ${generatedAt} &nbsp;·&nbsp; Internal use only
</div>
</body>
</html>`;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function VerdictChip({ verdict, size = 'small' }) {
  const map = {
    better:       { label: 'Better on Azure',    color: 'success' },
    'mostly-better': { label: 'Mostly better',   color: 'success', variant: 'outlined' },
    worse:        { label: 'Worse on Azure',      color: 'error' },
    'mostly-worse': { label: 'Mostly worse',      color: 'error', variant: 'outlined' },
    mixed:        { label: 'Mixed results',       color: 'warning' },
    'no-data':    { label: 'No Datadog data',     color: 'default' }
  };
  const cfg = map[verdict] || { label: verdict, color: 'default' };
  return (
    <Chip
      size={size}
      label={cfg.label}
      color={cfg.color}
      variant={cfg.variant || 'filled'}
      sx={{ fontWeight: 600, fontSize: 11 }}
    />
  );
}

function DeltaCell({ delta, verdict }) {
  const bg = verdict === 'better' ? GOOD_BG : verdict === 'worse' ? BAD_BG : NEUTRAL_BG;
  const fg = verdict === 'better' ? GOOD_FG : verdict === 'worse' ? BAD_FG : NEUTRAL_FG;
  const icon =
    verdict === 'better' ? <TrendingDownIcon sx={{ fontSize: 13, mr: 0.3 }} /> :
    verdict === 'worse'  ? <TrendingUpIcon sx={{ fontSize: 13, mr: 0.3 }} /> :
    null;

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: 0.8,
        py: 0.2,
        borderRadius: 1,
        bgcolor: bg,
        color: fg,
        fontWeight: 600,
        fontSize: 12,
        minWidth: 60,
        justifyContent: 'center'
      }}
    >
      {icon}{formatDelta(delta)}
    </Box>
  );
}

// ─── ClientCard ───────────────────────────────────────────────────────────────

const VERDICT_ORDER = { worse: 0, 'mostly-worse': 1, mixed: 2, 'mostly-better': 3, better: 4, 'no-data': 5 };

function ClientCard({ client, report }) {
  const [expanded, setExpanded] = useState(false);
  const status = report?.status ?? 'pending';
  const summary = report?.summary;
  const trend   = report?.trend ?? null;

  const isRecovering = trend?.direction === 'recovering';
  const isDegrading  = trend?.direction === 'degrading';

  // Recovery overrides the border to teal when the 30-day verdict is still bad/mixed
  const verdictIsBadOrMixed = ['worse', 'mostly-worse', 'mixed'].includes(summary?.overall);
  const showRecoveryBadge = isRecovering && verdictIsBadOrMixed;
  const showDegradingBadge = isDegrading && ['better', 'mostly-better'].includes(summary?.overall);

  const borderColor =
    showRecoveryBadge ? '#0097a7' :
    summary?.overall === 'better' || summary?.overall === 'mostly-better' ? GOOD_FG :
    summary?.overall === 'worse'  || summary?.overall === 'mostly-worse'  ? BAD_FG :
    summary?.overall === 'mixed'  ? '#e65100' : '#e0e0e0';

  const cardBg =
    showRecoveryBadge ? '#e0f7fa' :
    summary?.overall === 'better' || summary?.overall === 'mostly-better' ? '#f8fff9' :
    summary?.overall === 'worse'  || summary?.overall === 'mostly-worse'  ? '#fff8f8' : undefined;

  return (
    <Paper
      variant="outlined"
      sx={{ mb: 1.5, borderRadius: 2, borderLeft: '4px solid', borderColor, bgcolor: cardBg, overflow: 'hidden' }}
    >
      {/* ── Header ── */}
      <Box sx={{ px: 2, py: 1.25, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        {/* Instance info */}
        <Box sx={{ flex: 1, minWidth: 140 }}>
          {client.instanceUrl ? (
            <Typography
              component="a"
              href={client.instanceUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="body2"
              fontWeight={700}
              sx={{ color: 'primary.main', textDecoration: 'none', display: 'block', '&:hover': { textDecoration: 'underline' } }}
            >
              {client.instanceName}
            </Typography>
          ) : (
            <Typography variant="body2" fontWeight={700}>{client.instanceName}</Typography>
          )}
          <Stack direction="row" spacing={0.4} alignItems="center" mt={0.3}>
            <Chip size="small" label={client.sourceDatacenter || '—'} sx={{ fontSize: 10, height: 16, bgcolor: '#fff8e1', color: '#b45309', fontWeight: 700 }} />
            <Typography variant="caption" color="text.disabled" sx={{ lineHeight: 1 }}>→</Typography>
            <Chip size="small" label={client.currentDatacenter || '—'} sx={{ fontSize: 10, height: 16, bgcolor: '#e3f2fd', color: '#0d47a1', fontWeight: 700 }} />
          </Stack>
        </Box>

        {/* Right side: status, verdict, date, expand */}
        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
          {status === 'loading' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CircularProgress size={14} />
              <Typography variant="caption" color="text.secondary">Loading…</Typography>
            </Box>
          )}
          {status === 'ignored' && (
            <MuiTooltip arrow title={report?.reason || 'Source and target datacenter are the same — no comparison possible'}>
              <Chip size="small" label="Same DC" sx={{ fontSize: 10, bgcolor: '#f5f5f5', color: '#616161', fontWeight: 600 }} />
            </MuiTooltip>
          )}
          {status === 'error' && (
            <MuiTooltip arrow title={report?.error || 'Unknown error'}>
              <Chip size="small" label="Load failed" color="warning" sx={{ fontSize: 10 }} />
            </MuiTooltip>
          )}
          {status === 'pending' && (
            <Chip size="small" label="Not loaded" sx={{ fontSize: 10, bgcolor: NEUTRAL_BG, color: NEUTRAL_FG }} />
          )}
          {status === 'done' && !summary && (
            <Chip size="small" label="No Datadog data" color="default" sx={{ fontSize: 10 }} />
          )}
          {status === 'done' && summary && (
            <>
              <VerdictChip verdict={summary.overall} />
              <Chip
                size="small"
                label={`${summary.betterCount}↑ ${summary.worseCount}↓`}
                sx={{ fontSize: 10, height: 20, bgcolor: NEUTRAL_BG, color: NEUTRAL_FG, fontWeight: 600 }}
              />
              {showRecoveryBadge && (
                <MuiTooltip arrow title={
                  `Performance is recovering — ${trend.improving} key metric${trend.improving !== 1 ? 's' : ''} improved ≥8% since ${
                    trend.comparedDate ? format(new Date(trend.comparedDate), 'd MMM') : 'earlier'
                  }. The 30-day average still looks worse because of the rocky start, but recent days are trending better.`
                }>
                  <Chip
                    size="small"
                    label="↗ Recovering"
                    sx={{ fontSize: 10, height: 20, bgcolor: '#e0f7fa', color: '#006064', fontWeight: 700, border: '1px solid #80deea', cursor: 'help' }}
                  />
                </MuiTooltip>
              )}
              {showDegradingBadge && (
                <MuiTooltip arrow title={
                  `Watch out — ${trend.degrading} metric${trend.degrading !== 1 ? 's' : ''} that were better overall are now declining in recent days. The 30-day average still looks good but may worsen.`
                }>
                  <Chip
                    size="small"
                    label="↘ Degrading"
                    sx={{ fontSize: 10, height: 20, bgcolor: '#fff3e0', color: '#bf360c', fontWeight: 700, border: '1px solid #ffcc80', cursor: 'help' }}
                  />
                </MuiTooltip>
              )}
            </>
          )}
          {report?.snapshotDate && (
            <MuiTooltip arrow title={`Snapshot: ${format(new Date(report.snapshotDate), 'd MMM yyyy')}`}>
              <Chip size="small" label={format(new Date(report.snapshotDate), 'd MMM')} color="success" variant="outlined" sx={{ fontSize: 10 }} />
            </MuiTooltip>
          )}
          {status === 'done' && summary && (
            <IconButton size="small" onClick={() => setExpanded((v) => !v)} sx={{ p: 0.25 }}>
              {expanded ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
            </IconButton>
          )}
        </Stack>
      </Box>

      {/* ── Metric tiles ── */}
      {status === 'done' && summary && (
        <Box sx={{ px: 2, pb: 1.5 }}>
          <Grid container spacing={0.75}>
            {METRIC_DEFS.map((m) => {
              const metric = summary.metrics[m.key];
              const bg =
                metric.verdict === 'better' ? GOOD_BG :
                metric.verdict === 'worse'  ? BAD_BG  : NEUTRAL_BG;
              const fg =
                metric.verdict === 'better' ? GOOD_FG :
                metric.verdict === 'worse'  ? BAD_FG  : NEUTRAL_FG;
              const borderC =
                metric.verdict === 'better' ? '#a5d6a7' :
                metric.verdict === 'worse'  ? '#ef9a9a' : '#e0e0e0';
              const hasData = metric.colo != null && Number.isFinite(metric.colo) &&
                              metric.azure != null && Number.isFinite(metric.azure);
              // Trend arrow for this metric (positive delta = improving)
              const trendDelta = trend?.metricDeltas?.[m.key];
              const metricTrendUp   = trendDelta != null && trendDelta >= 8;
              const metricTrendDown = trendDelta != null && trendDelta <= -8;
              return (
                <Grid item xs={6} sm={4} md={2} key={m.key}>
                  <Box sx={{ p: 0.9, borderRadius: 1.5, bgcolor: bg, border: '1px solid', borderColor: borderC, height: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.4 }}>
                      <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: fg, lineHeight: 1.2 }}>
                        {m.label}
                      </Typography>
                      {metricTrendUp && (
                        <MuiTooltip arrow title={`Improving recently (+${trendDelta.toFixed(0)}% better vs ~${trend?.comparedDate ? format(new Date(trend.comparedDate), 'd MMM') : '5d ago'})`}>
                          <Typography sx={{ fontSize: 10, color: '#006064', fontWeight: 800, lineHeight: 1, cursor: 'help' }}>↗</Typography>
                        </MuiTooltip>
                      )}
                      {metricTrendDown && (
                        <MuiTooltip arrow title={`Degrading recently (${trendDelta.toFixed(0)}% worse vs ~${trend?.comparedDate ? format(new Date(trend.comparedDate), 'd MMM') : '5d ago'})`}>
                          <Typography sx={{ fontSize: 10, color: '#bf360c', fontWeight: 800, lineHeight: 1, cursor: 'help' }}>↘</Typography>
                        </MuiTooltip>
                      )}
                    </Box>
                    {hasData ? (
                      <>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, flexWrap: 'nowrap', mb: 0.4 }}>
                          <Box sx={{ px: 0.5, py: 0.15, borderRadius: 0.5, bgcolor: '#fde8df', color: COLO_COLOR, fontWeight: 700, fontSize: 9, whiteSpace: 'nowrap' }}>
                            {formatVal(metric.colo, m)}
                          </Box>
                          <Typography sx={{ fontSize: 9, color: 'text.disabled', lineHeight: 1 }}>→</Typography>
                          <Box sx={{ px: 0.5, py: 0.15, borderRadius: 0.5, bgcolor: '#d4eadf', color: AZURE_COLOR, fontWeight: 700, fontSize: 9, whiteSpace: 'nowrap' }}>
                            {formatVal(metric.azure, m)}
                          </Box>
                        </Box>
                        <DeltaCell delta={metric.delta} verdict={metric.verdict} />
                      </>
                    ) : (
                      <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>—</Typography>
                    )}
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}

      {/* ── Expanded detail table ── */}
      <Collapse in={expanded}>
        <Divider />
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" sx={{ mb: 1 }}>
            Detailed comparison — {summary?.betterCount} metric{summary?.betterCount !== 1 ? 's' : ''} improved · {summary?.worseCount} worsened
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontSize: 11, fontWeight: 700, py: 0.5, px: 1, bgcolor: '#f8f9fa' } }}>
                <TableCell>Metric</TableCell>
                <TableCell align="right" sx={{ color: COLO_COLOR }}>Before (Colo)</TableCell>
                <TableCell align="right" sx={{ color: AZURE_COLOR }}>After (Azure)</TableCell>
                <TableCell align="center">Change</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {METRIC_DEFS.map((m) => {
                const metric = summary?.metrics?.[m.key];
                return (
                  <TableRow key={m.key} sx={{ '& td': { py: 0.5, px: 1, fontSize: 11 } }}>
                    <TableCell>
                      <MuiTooltip arrow title={m.tooltip}>
                        <span style={{ cursor: 'help' }}>{m.label}</span>
                      </MuiTooltip>
                    </TableCell>
                    <TableCell align="right" sx={{ color: COLO_COLOR, fontWeight: 600 }}>
                      {formatVal(metric?.colo, m)}
                    </TableCell>
                    <TableCell align="right" sx={{ color: AZURE_COLOR, fontWeight: 600 }}>
                      {formatVal(metric?.azure, m)}
                    </TableCell>
                    <TableCell align="center">
                      {metric ? <DeltaCell delta={metric.delta} verdict={metric.verdict} /> : '—'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </Collapse>
    </Paper>
  );
}

/** Summary band: shows cohort-wide counts per metric + overall. */
function CohortSummaryBand({ reports, clients }) {
  const clientCount = clients.length;
  const doneReports = Object.values(reports).filter((r) => r.status === 'done' && r.summary);
  const errorReports = Object.values(reports).filter((r) => r.status === 'error');
  const ignoredReports = Object.values(reports).filter((r) => r.status === 'ignored');

  const metricTallies = useMemo(() => {
    return METRIC_DEFS.map((m) => {
      const tallied = { better: 0, worse: 0, similar: 0, noData: 0 };
      for (const r of doneReports) {
        const v = r.summary?.metrics?.[m.key]?.verdict ?? 'no-data';
        if (v === 'better') tallied.better++;
        else if (v === 'worse') tallied.worse++;
        else if (v === 'similar') tallied.similar++;
        else tallied.noData++;
      }
      return { ...m, ...tallied };
    });
  }, [doneReports]);

  const overallTally = useMemo(() => {
    const t = { better: 0, worse: 0, mixed: 0, noData: 0 };
    for (const r of doneReports) {
      const v = r.summary?.overall;
      if (v === 'better' || v === 'mostly-better') t.better++;
      else if (v === 'worse' || v === 'mostly-worse') t.worse++;
      else if (v === 'mixed') t.mixed++;
      else t.noData++;
    }
    return t;
  }, [doneReports]);

  if (doneReports.length === 0) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
        Cohort summary — {doneReports.length} of {clientCount} clients loaded
        {errorReports.length > 0 && (
          <Chip size="small" label={`${errorReports.length} failed`} color="warning" sx={{ ml: 1, fontWeight: 600, fontSize: 11 }} />
        )}
        {ignoredReports.length > 0 && (
          <Chip size="small" label={`${ignoredReports.length} ignored`} sx={{ ml: 1, fontWeight: 600, fontSize: 11, bgcolor: '#f5f5f5', color: '#616161' }} />
        )}
      </Typography>

      {/* Overall verdict row */}
      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
        <Grid item xs={6} sm={3}>
          <Box sx={{ p: 1.2, borderRadius: 1.5, bgcolor: GOOD_BG, textAlign: 'center' }}>
            <Typography variant="h5" fontWeight={800} color={GOOD_FG}>{overallTally.better}</Typography>
            <Typography variant="caption" color={GOOD_FG} fontWeight={600}>Overall better</Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box sx={{ p: 1.2, borderRadius: 1.5, bgcolor: BAD_BG, textAlign: 'center' }}>
            <Typography variant="h5" fontWeight={800} color={BAD_FG}>{overallTally.worse}</Typography>
            <Typography variant="caption" color={BAD_FG} fontWeight={600}>Overall worse</Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box sx={{ p: 1.2, borderRadius: 1.5, bgcolor: '#fff8e1', textAlign: 'center' }}>
            <Typography variant="h5" fontWeight={800} color="#e65100">{overallTally.mixed}</Typography>
            <Typography variant="caption" color="#e65100" fontWeight={600}>Mixed results</Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box sx={{ p: 1.2, borderRadius: 1.5, bgcolor: NEUTRAL_BG, textAlign: 'center' }}>
            <Typography variant="h5" fontWeight={800} color={NEUTRAL_FG}>{overallTally.noData + errorReports.length + ignoredReports.length}</Typography>
            <Typography variant="caption" color={NEUTRAL_FG} fontWeight={600}>No data / ignored</Typography>
          </Box>
        </Grid>
      </Grid>

      <Divider sx={{ my: 1.5 }} />

      {/* Per-metric mini-tallies */}
      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1 }}>
        Per-metric breakdown (of {doneReports.length} loaded)
      </Typography>
      <Grid container spacing={1}>
        {metricTallies.map((m) => (
          <Grid item xs={6} sm={3} key={m.key}>
            <Box sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
              <Typography variant="caption" fontWeight={700} color="text.primary" display="block">{m.label}</Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" mt={0.4}>
                <Chip size="small" label={`${m.better} better`} sx={{ bgcolor: GOOD_BG, color: GOOD_FG, fontSize: 10, fontWeight: 700, height: 18 }} />
                <Chip size="small" label={`${m.worse} worse`} sx={{ bgcolor: BAD_BG, color: BAD_FG, fontSize: 10, fontWeight: 700, height: 18 }} />
                {m.similar > 0 && <Chip size="small" label={`${m.similar} similar`} sx={{ bgcolor: NEUTRAL_BG, color: NEUTRAL_FG, fontSize: 10, fontWeight: 600, height: 18 }} />}
              </Stack>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const AzureMigrationWeeklyCohortTab = () => {
  const [progressData, setProgressData] = useState(null);
  const [progressLoading, setProgressLoading] = useState(true);
  const [progressError, setProgressError] = useState(null);

  const [selectedWeek, setSelectedWeek] = useState('');
  const DAYS = 30;

  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [clientsError, setClientsError] = useState(null);

  // { [instanceName]: { status: 'pending'|'loading'|'done'|'error', summary, error } }
  const [reports, setReports] = useState({});
  const [perfRunning, setPerfRunning] = useState(false);
  const [perfDone, setPerfDone] = useState(0);
  const [perfTotal, setPerfTotal] = useState(0);

  // ── Sync status (MongoDB-backed) ─────────────────────────────────────────────
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncTriggered, setSyncTriggered] = useState(false);
  const [loadingFromDb, setLoadingFromDb] = useState(false);
  const [refreshError, setRefreshError] = useState(null);
  const [refreshInfo, setRefreshInfo] = useState(null);
  const [backfillConfirmOpen, setBackfillConfirmOpen] = useState(false);

  // ── Filters ──────────────────────────────────────────────────────────────────
  const [filterSearch, setFilterSearch] = useState('');
  const [filterVerdict, setFilterVerdict] = useState('all');   // 'all'|'better'|'mostly-better'|'worse'|'mostly-worse'|'mixed'|'no-data'
  const [filterMetric, setFilterMetric] = useState('all');     // 'all' | metric key
  const [filterMetricVerdict, setFilterMetricVerdict] = useState('all'); // 'all'|'better'|'worse'

  // ── Export ───────────────────────────────────────────────────────────────────
  const [exportLoading, setExportLoading] = useState(false);

  // ── Load progress data (for week list) ──────────────────────────────────────

  const loadProgress = useCallback(async () => {
    setProgressLoading(true);
    setProgressError(null);
    try {
      const res = await getMigrationProgress();
      if (res?.success && res?.data) {
        setProgressData(res.data);
      } else {
        setProgressError('Could not load migration progress data.');
      }
    } catch (err) {
      setProgressError(err?.message || 'Failed to load weeks.');
    } finally {
      setProgressLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProgress();
  }, [loadProgress]);

  // ── Sync status: fetch once on mount, then poll while a sync is running ───────

  const fetchSyncStatus = useCallback(async () => {
    try {
      const res = await getMigrationPerfSyncStatus();
      if (res?.success) setSyncStatus(res.data);
    } catch (_) {}
  }, []);

  useEffect(() => { fetchSyncStatus(); }, [fetchSyncStatus]);

  // Poll every 3 s while the backend reports isRunning or we just triggered one
  useEffect(() => {
    if (!syncTriggered && !syncStatus?.isRunning) return;
    const id = setInterval(async () => {
      await fetchSyncStatus();
      // Stop polling once the backend is no longer running
      setSyncStatus((prev) => {
        if (prev && !prev.isRunning) setSyncTriggered(false);
        return prev;
      });
    }, 3000);
    return () => clearInterval(id);
  }, [syncTriggered, syncStatus?.isRunning, fetchSyncStatus]);

  // ── Available weeks from progress data ──────────────────────────────────────

  const availableWeeks = useMemo(() => {
    const arr = progressData?.azure?.migratedOverall || [];
    return arr
      .filter((w) => w.week && (w.count ?? 0) > 0)
      .sort((a, b) => b.week.localeCompare(a.week)); // most recent first
  }, [progressData]);

  // Auto-select the most recent week when the list first loads
  useEffect(() => {
    if (!selectedWeek && availableWeeks.length > 0) {
      setSelectedWeek(availableWeeks[0].week);
    }
  }, [availableWeeks, selectedWeek]);

  // ── Core fetch logic — accepts a list directly so it can be called immediately
  //    after load without waiting for React state to propagate. ─────────────────

  const fetchPerformanceForList = useCallback(async (list) => {
    if (!list.length) return;
    setPerfRunning(true);
    setPerfDone(0);
    setPerfTotal(list.length);

    // Mark every client as 'loading' up-front
    setReports(() => {
      const next = {};
      for (const c of list) next[c.instanceName] = { status: 'loading' };
      return next;
    });

    let completed = 0;
    let nextIndex = 0;

    async function worker() {
      while (nextIndex < list.length) {
        const idx = nextIndex++;
        const client = list[idx];
        try {
          const res = await getAzureMigrationPerformance({
            instanceName: client.instanceName,
            days: DAYS
          });
          if (res?.ignored) {
            setReports((prev) => ({
              ...prev,
              [client.instanceName]: { status: 'ignored', reason: res.message }
            }));
          } else {
            const summary = computeClientSummary(res);
            setReports((prev) => ({
              ...prev,
              [client.instanceName]: { status: 'done', summary, raw: res }
            }));
          }
        } catch (err) {
          setReports((prev) => ({
            ...prev,
            [client.instanceName]: {
              status: 'error',
              error: err?.message || 'Failed to load performance data'
            }
          }));
        }
        setPerfDone(++completed);
      }
    }

    const pool = Array.from({ length: Math.min(CONCURRENCY, list.length) }, () => worker());
    await Promise.all(pool);
    setPerfRunning(false);
  }, []);

  // ── Load clients then immediately fetch performance ──────────────────────────

  const handleLoadCohort = useCallback(async () => {
    if (!selectedWeek) return;
    setClientsLoading(true);
    setClientsError(null);
    setClients([]);
    setReports({});
    setPerfDone(0);
    setPerfTotal(0);
    setPerfRunning(false);
    setFilterSearch('');
    setFilterVerdict('all');
    setFilterMetric('all');
    setFilterMetricVerdict('all');
    setRefreshError(null);
    setRefreshInfo(null);
    try {
      // Try loading from MongoDB first (fast, no Datadog calls)
      const dbRes = await getMigrationPerfSnapshots(selectedWeek);
      const rows = dbRes?.data ?? [];

      if (rows.length > 0) {
        // We have stored snapshots — populate from DB
        const list = rows.map(({ snapshot: _snap, trend: _t, ...client }) => client);
        setClients(list);
        const newReports = {};
        for (const row of rows) {
          const { instanceName, snapshot, trend } = row;
          if (!snapshot) {
            newReports[instanceName] = { status: 'pending' };
          } else {
            const summary = computeClientSummary({
              summary: snapshot.summary,
              apache: snapshot.apache,
              meta: snapshot.meta
            });
            newReports[instanceName] = {
              status: 'done',
              summary,
              trend: trend || null,
              syncedAt: snapshot.syncedAt,
              snapshotDate: snapshot.snapshotDate
            };
          }
        }
        setReports(newReports);
      } else {
        // No snapshots yet — fall back to live Datadog fetch
        const liveRes = await getMigrationClientsByWeek(selectedWeek, 'azure', 'migrated');
        const list = liveRes?.data ?? [];
        setClients(list);
        if (list.length > 0) fetchPerformanceForList(list);
      }
    } catch (err) {
      setClientsError(err?.message || 'Failed to load cohort data.');
    } finally {
      setClientsLoading(false);
    }
  }, [selectedWeek, fetchPerformanceForList]);

  // ── Refresh: fetch from Datadog → store to MongoDB → reload table ────────────

  const handleRefresh = useCallback(async () => {
    if (!selectedWeek || syncStatus?.isRunning || syncTriggered) return;
    setSyncTriggered(true);
    setRefreshError(null);
    setRefreshInfo(null);
    try {
      await triggerMigrationPerfSync({ week: selectedWeek });
      // Refresh the sync status immediately so the UI reflects the in-progress
      // job before the 3-second polling tick lands.
      fetchSyncStatus();
      // Polling will pick up progress automatically; the auto-reload effect
      // below loads the table from DB once isRunning flips back to false.
    } catch (err) {
      console.error('Refresh error:', err);
      setSyncTriggered(false);
      setRefreshError(err?.message || 'Failed to start the Datadog sync. Please try again.');
    }
  }, [selectedWeek, syncStatus?.isRunning, syncTriggered, fetchSyncStatus]);

  // Backfill: sync ALL azure-migrated clients (no week scope) to capture whatever
  // Colo data is still in Datadog before more of it falls outside the 30-day window.
  const handleBackfillAll = useCallback(async () => {
    if (syncStatus?.isRunning || syncTriggered) return;
    setBackfillConfirmOpen(false);
    setSyncTriggered(true);
    setRefreshError(null);
    setRefreshInfo(null);
    try {
      await triggerMigrationPerfSync({});
      fetchSyncStatus();
    } catch (err) {
      console.error('Backfill error:', err);
      setSyncTriggered(false);
      setRefreshError(err?.message || 'Failed to start the backfill sync. Please try again.');
    }
  }, [syncStatus?.isRunning, syncTriggered, fetchSyncStatus]);

  // When a triggered sync finishes (isRunning → false), auto-load from DB.
  // We reload regardless of whether `clients` was already populated — clicking
  // Refresh without first clicking Load should still populate the table when
  // the sync completes, otherwise the page stays blank with no feedback.
  const prevIsRunning = React.useRef(false);
  useEffect(() => {
    if (prevIsRunning.current && syncStatus && !syncStatus.isRunning) {
      if (syncTriggered) {
        const synced = syncStatus.lastSyncCount ?? 0;
        const errCount = syncStatus.lastSyncErrors ?? 0;
        setRefreshInfo(
          `Refresh complete — ${synced} client${synced !== 1 ? 's' : ''} synced` +
            (errCount > 0 ? `, ${errCount} error${errCount !== 1 ? 's' : ''}` : '') +
            '. Loading data...'
        );
      }
      handleLoadFromDb();
    }
    prevIsRunning.current = syncStatus?.isRunning ?? false;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncStatus?.isRunning]);

  // ── Load from MongoDB ─────────────────────────────────────────────────────────

  const handleLoadFromDb = useCallback(async () => {
    if (!selectedWeek) return;
    setLoadingFromDb(true);
    setClientsError(null);
    try {
      const res = await getMigrationPerfSnapshots(selectedWeek);
      const rows = res?.data ?? [];

      // Separate clients from their snapshots and populate the reports map
      const list = rows.map(({ snapshot: _snap, trend: _t, ...client }) => client);
      setClients(list);

      const newReports = {};
      for (const row of rows) {
        const { instanceName, snapshot, trend } = row;
        if (!snapshot) {
          newReports[instanceName] = { status: 'pending' };
        } else {
          const summary = computeClientSummary({
            summary: snapshot.summary,
            apache: snapshot.apache,
            meta: snapshot.meta
          });
          newReports[instanceName] = {
            status: 'done',
            summary,
            trend: trend || null,
            syncedAt: snapshot.syncedAt,
            snapshotDate: snapshot.snapshotDate
          };
        }
      }
      setReports(newReports);
    } catch (err) {
      setClientsError(err?.message || 'Failed to load snapshots from database.');
    } finally {
      setLoadingFromDb(false);
    }
  }, [selectedWeek]);

  // ── Live Datadog fetch (retry / first-time if no DB data) ────────────────────

  const handleFetchPerformance = useCallback(() => {
    if (clients.length && !perfRunning) fetchPerformanceForList(clients);
  }, [clients, perfRunning, fetchPerformanceForList]);

  // ── Export HTML cohort report ─────────────────────────────────────────────────

  const handleExportHtml = useCallback(() => {
    if (!clients.length || !selectedWeek) return;
    setExportLoading(true);
    try {
      const html = generateCohortReportHtml(clients, reports, selectedWeek);
      const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cohort-report-${selectedWeek}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } finally {
      setExportLoading(false);
    }
  }, [clients, reports, selectedWeek]);

  // ── Derived values (must be before any early return) ─────────────────────────

  const filteredClients = useMemo(() => {
    return clients.filter((client) => {
      // Name search
      if (filterSearch) {
        const q = filterSearch.toLowerCase();
        if (!client.instanceName.toLowerCase().includes(q)) return false;
      }
      // Overall verdict filter
      if (filterVerdict !== 'all') {
        const report = reports[client.instanceName];
        const overall = report?.summary?.overall ?? 'no-data';
        // 'better' catches both 'better' and 'mostly-better'; 'worse' catches both worse variants
        if (filterVerdict === 'better' && overall !== 'better' && overall !== 'mostly-better') return false;
        if (filterVerdict === 'worse' && overall !== 'worse' && overall !== 'mostly-worse') return false;
        if (filterVerdict !== 'better' && filterVerdict !== 'worse' && overall !== filterVerdict) return false;
      }
      // Per-metric verdict filter
      if (filterMetric !== 'all' && filterMetricVerdict !== 'all') {
        const report = reports[client.instanceName];
        const mVerdict = report?.summary?.metrics?.[filterMetric]?.verdict ?? 'no-data';
        if (filterMetricVerdict === 'better' && mVerdict !== 'better') return false;
        if (filterMetricVerdict === 'worse' && mVerdict !== 'worse') return false;
      }
      return true;
    });
  }, [clients, reports, filterSearch, filterVerdict, filterMetric, filterMetricVerdict]);

  // ── Early returns (after all hooks) ──────────────────────────────────────────

  if (progressLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4 }}>
        <CircularProgress size={24} />
        <Typography color="text.secondary">Loading migration weeks...</Typography>
      </Box>
    );
  }

  if (progressError) {
    return (
      <Alert severity="error" action={
        <Button size="small" onClick={loadProgress} startIcon={<RefreshIcon />}>Retry</Button>
      }>
        {progressError}
      </Alert>
    );
  }

  const selectedWeekEntry = availableWeeks.find((w) => w.week === selectedWeek);
  const perfPct = perfTotal > 0 ? Math.round((perfDone / perfTotal) * 100) : 0;

  return (
    <Box>
      {/* ── Header ── */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" fontWeight={700} gutterBottom>
          Weekly Migration Cohort Performance
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Select a migration week to see all clients that moved to Azure that week, then load their
          Datadog performance metrics side-by-side to understand how the cohort performed after cutover.
        </Typography>
      </Box>

      {/* ── Controls ── */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
          {/* Week picker */}
          <FormControl size="small" sx={{ minWidth: 280, flexShrink: 0 }}>
            <InputLabel>Migration week</InputLabel>
            <Select
              label="Migration week"
              value={selectedWeek}
              onChange={(e) => {
                setSelectedWeek(e.target.value);
                setClients([]);
                setReports({});
                setRefreshError(null);
                setRefreshInfo(null);
              }}
            >
              {availableWeeks.map((w) => (
                <MenuItem key={w.week} value={w.week}>
                  {formatWeekRange(w.week)}
                  <Chip
                    size="small"
                    label={`${w.count} client${w.count !== 1 ? 's' : ''}`}
                    sx={{ ml: 1, fontSize: 10, height: 18 }}
                  />
                </MenuItem>
              ))}
              {availableWeeks.length === 0 && (
                <MenuItem disabled>No migration weeks found</MenuItem>
              )}
            </Select>
          </FormControl>

          {/* Action buttons */}
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            {/* Load — reads from MongoDB */}
            <Button
              variant="contained"
              disabled={!selectedWeek || clientsLoading || loadingFromDb}
              onClick={handleLoadCohort}
              startIcon={(clientsLoading || loadingFromDb) ? <CircularProgress size={14} color="inherit" /> : null}
              sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
            >
              {clientsLoading || loadingFromDb ? 'Loading...' : 'Load'}
            </Button>

            {/* Refresh — fetches from Datadog, stores to MongoDB (scoped to selected week) */}
            <MuiTooltip arrow title="Fetch latest data from Datadog and save to database for the selected week.">
              <span>
                <Button
                  variant="outlined"
                  color="primary"
                  disabled={!selectedWeek || syncStatus?.isRunning || syncTriggered || clientsLoading}
                  onClick={handleRefresh}
                  startIcon={(syncStatus?.isRunning || syncTriggered)
                    ? <CircularProgress size={14} color="inherit" />
                    : <RefreshIcon />}
                  sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
                >
                  {syncStatus?.isRunning || syncTriggered ? 'Syncing...' : 'Refresh'}
                </Button>
              </span>
            </MuiTooltip>

            {/* Backfill All — syncs every azure-migrated client to capture historical Colo data */}
            <MuiTooltip arrow title="Snapshot ALL azure-migrated clients right now — captures remaining Colo data before Datadog's 30-day window expires. Run once to preserve historical baselines.">
              <span>
                <Button
                  variant="outlined"
                  color="warning"
                  disabled={syncStatus?.isRunning || syncTriggered || clientsLoading}
                  onClick={() => setBackfillConfirmOpen(true)}
                  startIcon={(syncStatus?.isRunning || syncTriggered)
                    ? <CircularProgress size={14} color="inherit" />
                    : <RefreshIcon />}
                  sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
                >
                  {syncStatus?.isRunning || syncTriggered ? 'Syncing...' : 'Backfill All Clients'}
                </Button>
              </span>
            </MuiTooltip>

            {/* Live Datadog spinner */}
            {perfRunning && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                <CircularProgress size={14} />
                <Typography variant="caption" color="text.secondary">
                  Live fetch {perfDone}/{perfTotal}
                </Typography>
              </Box>
            )}
          </Stack>

          {/* Fixed window badge */}
          <Chip
            size="small"
            label={`${DAYS}-day window`}
            variant="outlined"
            sx={{ alignSelf: { xs: 'flex-start', sm: 'center' }, fontSize: 11 }}
          />
        </Stack>

        {/* ── Sync status strip ── */}
        {syncStatus && (
          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: syncStatus.isRunning ? '#f59e0b' : '#4caf50', flexShrink: 0 }} />
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                {syncStatus.isRunning
                  ? `Syncing… ${syncStatus.progress?.done ?? 0} / ${syncStatus.progress?.total ?? '?'} clients`
                  : 'Daily auto-sync active (08:00 IST · all clients)'}
              </Typography>
            </Box>
            {syncStatus.lastSyncAt && (
              <Typography variant="caption" color="text.secondary">
                Last synced:{' '}
                <strong>
                  {format(new Date(syncStatus.lastSyncAt), 'd MMM yyyy, HH:mm')} UTC
                </strong>
                {syncStatus.lastSyncCount != null && ` — ${syncStatus.lastSyncCount} client${syncStatus.lastSyncCount !== 1 ? 's' : ''}`}
                {syncStatus.lastSyncErrors > 0 && `, ${syncStatus.lastSyncErrors} error${syncStatus.lastSyncErrors !== 1 ? 's' : ''}`}
              </Typography>
            )}
            {syncStatus.nextSyncAt && !syncStatus.isRunning && (
              <MuiTooltip title="Runs daily at 08:00 IST (02:30 UTC) for all azure-migrated clients">
                <Typography variant="caption" color="text.secondary" sx={{ cursor: 'help' }}>
                  Next sync:{' '}
                  <strong>
                    {format(new Date(syncStatus.nextSyncAt), 'd MMM yyyy, HH:mm')} UTC
                  </strong>
                </Typography>
              </MuiTooltip>
            )}
            {syncStatus.totalSnapshots > 0 && (
              <Chip
                size="small"
                label={`${syncStatus.totalSnapshots.toLocaleString()} snapshots stored`}
                sx={{ fontSize: 10, height: 18 }}
              />
            )}
          </Box>
        )}

        {/* Info pill */}
        {selectedWeekEntry && (
          <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              size="small"
              label={`Week of ${formatWeekRange(selectedWeek)}`}
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 600 }}
            />
            <Chip
              size="small"
              label={`${selectedWeekEntry.count} client${selectedWeekEntry.count !== 1 ? 's' : ''} migrated`}
            />
          </Box>
        )}

        {/* Progress bar (inside the controls card, below the row) */}
        {(perfRunning || (perfTotal > 0 && perfDone < perfTotal)) && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" fontWeight={600}>
                Fetching Datadog metrics... {perfDone} / {perfTotal} clients
              </Typography>
              <Typography variant="caption" color="text.secondary">{perfPct}%</Typography>
            </Box>
            <LinearProgress variant="determinate" value={perfPct} sx={{ borderRadius: 1, height: 6 }} />
          </Box>
        )}
      </Paper>

      {/* ── Errors ── */}
      {clientsError && (
        <Alert severity="error" sx={{ mb: 2 }}>{clientsError}</Alert>
      )}
      {refreshError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setRefreshError(null)}>
          {refreshError}
        </Alert>
      )}
      {refreshInfo && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setRefreshInfo(null)}>
          {refreshInfo}
        </Alert>
      )}

      {/* ── No clients loaded yet ── */}
      {!clientsLoading && !loadingFromDb && clients.length === 0 && !clientsError && (
        <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
          {(syncStatus?.isRunning || syncTriggered) ? (
            <>
              <CircularProgress size={20} sx={{ mb: 1 }} />
              <Typography variant="body1">
                Sync in progress
                {syncStatus?.progress?.total
                  ? ` — ${syncStatus.progress.done ?? 0} / ${syncStatus.progress.total} clients`
                  : '...'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                The cohort will load automatically once the Datadog sync finishes.
              </Typography>
            </>
          ) : (
            <Typography variant="body1">
              Select a week above and click <strong>Load</strong> to view stored snapshots,
              or <strong>Refresh</strong> to fetch the latest data from Datadog.
            </Typography>
          )}
        </Box>
      )}

      {/* ── Cohort summary band ── */}
      {clients.length > 0 && (
        <CohortSummaryBand reports={reports} clients={clients} />
      )}

      {/* ── Client table ── */}
      {clients.length > 0 && (
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>

          {/* ── Table header bar ── */}
          <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Typography variant="subtitle2" fontWeight={700}>
                {filteredClients.length !== clients.length
                  ? `${filteredClients.length} of ${clients.length} clients`
                  : `${clients.length} client${clients.length !== 1 ? 's' : ''}`
                } — week of {formatWeekRange(selectedWeek)}
              </Typography>
              {filteredClients.length !== clients.length && (
                <Chip
                  size="small"
                  label="filtered"
                  onDelete={() => { setFilterSearch(''); setFilterVerdict('all'); setFilterMetric('all'); setFilterMetricVerdict('all'); }}
                  sx={{ fontSize: 10 }}
                />
              )}
              <Box sx={{ flex: 1 }} />
              <Button
                variant="outlined"
                size="small"
                startIcon={exportLoading ? <CircularProgress size={13} color="inherit" /> : <DownloadIcon />}
                onClick={handleExportHtml}
                disabled={exportLoading || !clients.length}
                sx={{ textTransform: 'none', fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap' }}
              >
                Export HTML
              </Button>
              <MuiTooltip
                arrow
                title={
                  <Box sx={{ p: 0.5, maxWidth: 380 }}>
                    <Typography variant="caption" display="block" gutterBottom fontWeight={700}>How to read this table</Typography>
                    <Typography variant="caption" display="block" gutterBottom>
                      Each row is one client. % columns show how Azure compares to Colo over the {DAYS}-day window.
                    </Typography>
                    <Typography variant="caption" display="block" gutterBottom>
                      <strong style={{ color: GOOD_FG }}>Green</strong> = Azure improved &nbsp;&middot;&nbsp;
                      <strong style={{ color: BAD_FG }}>Red</strong> = Azure worse &nbsp;&middot;&nbsp;
                      Grey = within noise band.
                    </Typography>
                    <Typography variant="caption" display="block">
                      Bands: p99 &plusmn;15% &middot; Error &plusmn;25% &middot; HTTP &plusmn;15% &middot; Memory &plusmn;10% &middot; API/UI &plusmn;15%.
                      Very large changes show as ratio (e.g. 9.5&times;&uarr;).
                      API &amp; UI columns are from Apache access logs (real user traffic).
                    </Typography>
                  </Box>
                }
              >
                <HelpOutlineIcon sx={{ fontSize: 16, color: 'text.disabled', cursor: 'help' }} />
              </MuiTooltip>
            </Box>

            {/* ── Filters ── */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }} flexWrap="wrap">
              {/* Name search */}
              <Box sx={{ position: 'relative', minWidth: 200 }}>
                <input
                  value={filterSearch}
                  onChange={(e) => setFilterSearch(e.target.value)}
                  placeholder="Search instance..."
                  style={{
                    width: '100%',
                    padding: '6px 10px',
                    fontSize: 12,
                    border: '1px solid #ccc',
                    borderRadius: 6,
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </Box>

              {/* Overall verdict filter */}
              <Stack direction="row" spacing={0.5} flexWrap="wrap">
                {[
                  { value: 'all',    label: 'All' },
                  { value: 'better', label: 'Better',  color: GOOD_FG, bg: GOOD_BG },
                  { value: 'worse',  label: 'Worse',   color: BAD_FG,  bg: BAD_BG  },
                  { value: 'mixed',  label: 'Mixed',   color: '#e65100', bg: '#fff8e1' },
                  { value: 'no-data',label: 'No data', color: NEUTRAL_FG, bg: NEUTRAL_BG }
                ].map((opt) => (
                  <Chip
                    key={opt.value}
                    size="small"
                    label={opt.label}
                    onClick={() => setFilterVerdict(filterVerdict === opt.value ? 'all' : opt.value)}
                    sx={{
                      fontSize: 11,
                      fontWeight: filterVerdict === opt.value ? 700 : 400,
                      cursor: 'pointer',
                      bgcolor: filterVerdict === opt.value ? (opt.bg || '#e3f2fd') : undefined,
                      color: filterVerdict === opt.value ? (opt.color || '#0d47a1') : undefined,
                      border: filterVerdict === opt.value ? `1.5px solid ${opt.color || '#0d47a1'}` : '1px solid transparent'
                    }}
                    variant="outlined"
                  />
                ))}
              </Stack>

              {/* Per-metric filter */}
              <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                <FormControl size="small" sx={{ minWidth: 130 }}>
                  <Select
                    value={filterMetric}
                    onChange={(e) => { setFilterMetric(e.target.value); setFilterMetricVerdict('all'); }}
                    displayEmpty
                    sx={{ fontSize: 12, height: 30 }}
                  >
                    <MenuItem value="all" sx={{ fontSize: 12 }}>Any metric</MenuItem>
                    {METRIC_DEFS.map((m) => (
                      <MenuItem key={m.key} value={m.key} sx={{ fontSize: 12 }}>{m.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {filterMetric !== 'all' && (
                  <Stack direction="row" spacing={0.5}>
                    {[
                      { value: 'better', label: 'Improved', color: GOOD_FG, bg: GOOD_BG },
                      { value: 'worse',  label: 'Worsened', color: BAD_FG,  bg: BAD_BG  }
                    ].map((opt) => (
                      <Chip
                        key={opt.value}
                        size="small"
                        label={opt.label}
                        onClick={() => setFilterMetricVerdict(filterMetricVerdict === opt.value ? 'all' : opt.value)}
                        sx={{
                          fontSize: 11,
                          fontWeight: filterMetricVerdict === opt.value ? 700 : 400,
                          cursor: 'pointer',
                          bgcolor: filterMetricVerdict === opt.value ? opt.bg : undefined,
                          color: filterMetricVerdict === opt.value ? opt.color : undefined,
                          border: filterMetricVerdict === opt.value ? `1.5px solid ${opt.color}` : '1px solid transparent'
                        }}
                        variant="outlined"
                      />
                    ))}
                  </Stack>
                )}
              </Stack>
            </Stack>
          </Box>

          {/* ── Client cards ── */}
          <Box sx={{ p: 2 }}>
            {filteredClients.length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                No clients match the current filters.
              </Box>
            ) : (
              [...filteredClients]
                .sort((a, b) => {
                  const oa = VERDICT_ORDER[reports[a.instanceName]?.summary?.overall ?? 'no-data'] ?? 5;
                  const ob = VERDICT_ORDER[reports[b.instanceName]?.summary?.overall ?? 'no-data'] ?? 5;
                  return oa - ob;
                })
                .map((client) => (
                  <ClientCard key={client.instanceName} client={client} report={reports[client.instanceName]} />
                ))
            )}
          </Box>

        </Paper>
      )}

      {/* ── Legend / how-to accordion ── */}
      <Accordion disableGutters variant="outlined" sx={{ mt: 3, borderRadius: '8px !important' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="body2" fontWeight={600}>How does this work?</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" paragraph>
            <strong>Step 1 — Pick a week.</strong> The week dropdown lists every week in which at least one
            client was migrated to Azure. Weeks come from the migration tracking spreadsheet.
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            <strong>Step 2 — Load.</strong> Clicking <strong>Load</strong> reads the most recent stored snapshot
            for each client from the database (fast, no Datadog calls). If no snapshot exists yet for a client
            it falls back to a live Datadog fetch automatically.
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            <strong>Refresh.</strong> Clicking <strong>Refresh</strong> triggers a background Datadog fetch
            for all clients in the selected week and saves the results to the database. Each client gets one
            snapshot per calendar day, so running Refresh on different days builds up a history you can
            review even after Datadog's 30-day retention window expires. The page reloads automatically once
            the sync finishes.
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            <strong>Daily auto-sync.</strong> A background job runs every night at 02:00 UTC and automatically
            syncs all clients migrated in the last {30} days — so the data stays up to date without manual
            intervention. The sync status strip below the controls shows the last and next scheduled run.
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            <strong>Reading the table.</strong> Each column shows how Azure compares to Colo as a % change.
            Negative % for speed/error metrics means Azure was lower (better). Positive % for memory means
            Azure had more free memory (better). Cells are colour-coded: green = Azure improved,
            red = Azure worse, grey = within the expected noise band.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Overall verdict</strong> is driven primarily by <strong>response time</strong> metrics —
            App p99, Apache API avg, and Apache UI avg. If the majority of those three are worse on Azure, the
            client is marked "Worse on Azure" regardless of how error rate or memory compare. Error rate,
            outbound HTTP, and memory are supporting indicators shown in the table but do not override a poor
            response-time result. When response-time metrics are split, all six metrics are used as a tiebreaker.
            Metrics with no data are excluded from the count so they don't skew the verdict.
          </Typography>
        </AccordionDetails>
      </Accordion>
      {/* ── Backfill confirmation dialog ── */}
      <Dialog open={backfillConfirmOpen} onClose={() => setBackfillConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Backfill All Clients — Confirm</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            This will snapshot <strong>every azure-migrated client</strong> right now using today's 30-day
            Datadog window. It runs across all clients in the latest migration upload (not just the selected week).
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            <strong>Why run this?</strong> Clients migrated 31–60 days ago still have partial Colo data
            in Datadog today — but that data disappears permanently as each day rolls past the 30-day
            retention window. Running this now captures whatever Colo baseline is still available before
            it expires.
          </Typography>
          <Alert severity="warning" sx={{ mt: 1 }}>
            This may take several minutes depending on how many clients are in the migration sheet.
            The daily cron already runs at 08:00 IST — only trigger this manually if you need an
            immediate backfill outside the scheduled window.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBackfillConfirmOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleBackfillAll}
            sx={{ textTransform: 'none' }}
          >
            Run Backfill Now
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default AzureMigrationWeeklyCohortTab;
